import { apiClient } from "@/api/api-client";
import { Button } from "@/components/core/buttons/button";
import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";

const colors = {
	pending: "#f87171",
	withDecision: "#eab308",
	withAppeal: "#3b82f6",
	caseClosed: "#82ca9d",
};

interface Tracker {
	level: number;
	index1: number | null;
	index2: number | null;
	index3: number | null;
}

const CasePerPilotCourt = () => {
	const refs = useRef<Array<ReactECharts | null>>([]);
	const [viewTable, setViewTable] = useState<boolean>(false);
	const [dataTable, setDataTable] = useState<any | null>(null);
	const tracker = useRef<Tracker>({
		level: 0,
		index1: null,
		index2: null,
		index3: null,
	});

	const staticOptions = {
		tooltip: {
			trigger: "axis",
			axisPointer: {
				type: "shadow",
			},
		},
		legend: {
			right: 0,
			top: 10,
		},
		grid: {
			left: "3%",
			right: "4%",
			bottom: "3%",
			containLabel: true,
		},
	};

	const [statistics, setStatistics] = useState<any>({});

	const fetchStatistics = async () => {
		const response = await apiClient.get("/backoffice/cases-per-pilot-court");

		if (response && response.data) {
			const temp: any = [];

			response.data.map((item: any) => {
				temp.push({
					...staticOptions,
					title: {
						text: "",
						extra: {
							court_station_code: item.court_station_code,
							total_count: item.total_count,
							max_number: item.max_number,
						},
						top: 10,
						left: 0,
						textStyle: {
							fontSize: 14,
						},
					},
					xAxis: {
						type: "value",
						boundaryGap: [0, 0.01],
						minInterval: 1,
					},
					yAxis: {
						type: "category",
						inverse: true,
						position: "left",
						data: item.branches.map((item) => {
							return {
								value: item,
							};
						}),
					},
					series: [
						{
							name: "Pending w/o Decision",
							type: "bar",
							data: item.pending_count,
							itemStyle: {
								color: colors.pending,
							},
						},
						{
							name: "Pending w/ Decision",
							type: "bar",
							data: item.with_decision_count,
							itemStyle: {
								color: colors.withDecision,
							},
						},
						{
							name: "With Appeal",
							type: "bar",
							data: item.with_appeal_count,
							itemStyle: {
								color: colors.withAppeal,
							},
						},
						{
							name: "Case Closed",
							type: "bar",
							data: item.case_closed_count,
							itemStyle: {
								color: colors.caseClosed,
							},
						},
					],
					graphic: [
						{
							type: "text",
							left: 5,
							bottom: 10,
							style: {
								text: "",
								fontSize: 14,
								backgroundColor: "none",
								// height: 34,
							},
							onclick: () => {},
						},
					],
					case_data: item.case_data,
				});

				setStatistics(temp);
			});
		}
	};

	const goBack = (index: number) => {
		refs.current[index]?.getEchartsInstance()?.setOption(statistics[index]);
		// updateTracker("prev");
	};

	const goToCaseCategory = (index: number, index2: number) => {
		refs.current[index]?.getEchartsInstance()?.setOption({
			...staticOptions,
			title: {
				text: statistics[index].yAxis.data[index2].value,
				top: 10,
				left: 0,
				textStyle: {
					fontSize: 14,
				},
			},
			legend: {
				right: 0,
				top: 10,
			},
			xAxis: {
				type: "value",
				boundaryGap: [0, 0.01],
				minInterval: 1,
			},
			yAxis: {
				type: "category",
				scale: false,
				data: statistics[index].case_data[index2].cases,
			},
			series: [
				{
					name: "Pending w/o Decision",
					type: "bar",
					data: statistics[index].case_data[index2].pending_count,
					itemStyle: {
						color: colors.pending,
					},
				},
				{
					name: "Pending w/ Decision",
					type: "bar",
					data: statistics[index].case_data[index2].with_decision_count,
					itemStyle: {
						color: colors.withDecision,
					},
				},
			],
			graphic: [
				{
					type: "text",
					left: 5,
					bottom: 10,
					style: {
						text: "Back",
						fontSize: 14,
						// height: 34,
						fontWeight: "600",
						fill: "#fff",
						textVerticalAlign: "center",
						padding: [12, 12, 12, 12],
						borderRadius: 6,
						backgroundColor: "hsla(279, 42%, 45%)",
					},
					onclick: () => {
						goBack(index);
					},
				},
			],
		});
		// updateTracker("next");
	};

	const goToCaseTypes = (index: number, index2: number, index3: number) => {
		refs.current[index]?.getEchartsInstance()?.setOption({
			...staticOptions,
			title: {
				text: statistics[index].case_data[index2].case_type_data[index3],
				top: 10,
				left: 0,
				textStyle: {
					fontSize: 14,
				},
			},
			legend: {
				right: 0,
				top: 10,
			},
			xAxis: {
				type: "value",
				boundaryGap: [0, 0.01],
				minInterval: 1,
			},
			yAxis: {
				type: "category",
				data: statistics[index].case_data[index2].case_type_data[index3].types,
			},
			series: [
				{
					name: "Pending w/o Decision",
					type: "bar",
					data: statistics[index].case_data[index2].case_type_data[index3].pending_count,
					itemStyle: {
						color: colors.pending,
					},
				},
				{
					name: "Pending w/ Decision",
					type: "bar",
					data: statistics[index].case_data[index2].case_type_data[index3].with_decision_count,
					itemStyle: {
						color: colors.withDecision,
					},
				},
			],
			graphic: [
				{
					type: "text",
					left: 5,
					bottom: 10,
					style: {
						text: "Back",
						fontSize: 14,
						// height: 34,
						fontWeight: "600",
						fill: "#fff",
						textVerticalAlign: "center",
						padding: [12, 12, 12, 12],
						borderRadius: 6,
						backgroundColor: "hsla(279, 42%, 45%)",
					},
					onclick: () => {
						goBack(index);
					},
				},
			],
		});
		updateTracker("next");
	};

	const computeChartHeight = (yAxisCount = 1) => {
		let height = 80;
		height = height + yAxisCount * 40;

		return `${height}px`;
	};

	const getCourtName = (courtCode: string) => {
		switch (courtCode) {
			case "RTC_BUL":
				return "RTC Malolos CIty";
				break;
			case "RTC_LPN":
				return "RTC Las Pinas City";
				break;
			case "MTCC_RXP":
				return "MTCC Roxas City";
				break;
			case "MTCC_TGM":
				return "MTC Tagum City";
				break;
			default:
				return "RTC Baguio City";
				break;
		}
	};

	const updateTracker = (action: string) => {
		const x: Tracker = tracker.current;
		if (action === "next") {
			tracker.current.level = x.level < 2 ? x.level + 1 : x.level;
		}
		if (action === "prev") {
			tracker.current.level = x.level > 1 ? x.level - 1 : 0;
		}

		console.log(tracker.current);
	};

	const handleBackTable = () => {
		setDataTable(null);
		tracker.current = {
			level: 0,
			index1: null,
			index2: null,
			index3: null,
		};
	};

	useEffect(() => {
		fetchStatistics();
	}, []);

	return (
		<div className="grid grid-cols-12 w-full gap-4">
			{statistics &&
				statistics.length > 0 &&
				statistics.map((item: any, index: number) => (
					<div className="col-span-12" key={"pilot-court-" + index}>
						<div className="flex flex-col w-full gap-4">
							<div className="flex flex-col w-full h-auto rounded-md border border-border/50 overflow-hidden">
								<div className="flex flex-row w-full p-3 border-b border-border/50 items-center">
									<div className="flex flex-col w-6/12 text-base font-semibold text-foreground-dark">{getCourtName(item.title.extra.court_station_code)}</div>
									<div className="flex text-right flex-col w-6/12 text-base font-semibold text-foreground-dark">
										Total Cases: {item.title.extra.total_count}
									</div>
								</div>

								{tracker.current.index1 === index && dataTable ? (
									<div className="flex flex-col w-full relative">
										<table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400 ">
											<thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
												<tr>
													<th className="px-6 py-3 w-[300px]">Case Type</th>
													<th className="px-6 py-3 text-right">Pending w/o Decision</th>
													<th className="px-6 py-3 text-right">Pending w/ Decision</th>
													<th className="px-6 py-3 text-right">With Appeal</th>
													<th className="px-6 py-3 text-right">Case Closed</th>
												</tr>
											</thead>
											<tbody>
												{dataTable.types.map((item2: any, index2: any) => (
													<tr key={index2} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200">
														<td className="px-6 py-4 font-medium text-gray-900 dark:text-white">{item2}</td>
														<td className="px-6 py-4 text-right">{dataTable.pending_count[index2]}</td>
														<td className="px-6 py-4 text-right">{dataTable.with_decision_count[index2]}</td>
														<td className="px-6 py-4 text-right">{dataTable.with_appeal_count[index2]}</td>
														<td className="px-6 py-4 text-right">{dataTable.case_closed_count[index2]}</td>
													</tr>
												))}
											</tbody>
										</table>
										<div className="p-3">
											<Button
												onClick={() => {
													handleBackTable();
												}}
												variant="default"
											>
												Back
											</Button>
										</div>
									</div>
								) : (
									<div className="w-full h-full shrink-0 px-2">
										<div
											style={{
												width: "100%",
												height: computeChartHeight(item.yAxis.data?.length),
											}}
										>
											<ReactECharts
												key={index}
												option={item}
												ref={(el) => (refs.current[index] = el)}
												style={{ height: "100%", width: "100%" }}
												onEvents={{
													click: (event: any) => {
														const prevTracker = tracker.current;

														if (event.componentType !== "graphic") {
															if (tracker.current.level == 1) {
																tracker.current = {
																	level: 2,
																	index1: prevTracker.index1,
																	index2: prevTracker.index2,
																	index3: event.dataIndex,
																};
																setDataTable(statistics[tracker.current.index1].case_data[tracker.current.index2].case_type_data[tracker.current.index3]);
															} else {
																tracker.current = {
																	level: 1,
																	index1: index,
																	index2: event.dataIndex,
																	index3: null,
																};
																goToCaseCategory(index, event.dataIndex);
															}
														}
													},
												}}
											/>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				))}
		</div>
	);
};

export default CasePerPilotCourt;
